import React, { useEffect, useRef, useState } from "react";
import { useCreateGameTopic, useGameTopics } from "@seabrookstudios/pitch2table";
import { useSnackbar } from "notistack";
import {
  buildNestiFyzComponentsTopicBody,
  buildNestiFyzOverviewTopicBody,
  findExistingImportedGameTopic,
  hasImportableComponentsTopic,
  hasImportableOverviewTopic,
  NESTIFYZ_COMPONENTS_TOPIC_NAME,
  NESTIFYZ_OVERVIEW_TOPIC_NAME,
} from "./gameTopicImportUtils";

/**
 * @param {object} props
 * @param {() => void} props.onComplete
 */
const CompleteOnMount = ({ onComplete }) => {
  const hasCalled = useRef(false);

  useEffect(() => {
    if (hasCalled.current) {
      return;
    }

    hasCalled.current = true;
    onComplete();
  }, [onComplete]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {string} props.topicName
 * @param {string} props.body
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const CreateImportedGameTopic = ({ gameId, topicName, body, onStart, onComplete, onError }) => {
  const { data: topics = [] } = useGameTopics(gameId);
  const hasStarted = useRef(false);
  const createGameTopic = useCreateGameTopic(gameId);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;
    onStart();

    const existingTopic = findExistingImportedGameTopic(topics, topicName);

    if (existingTopic) {
      onComplete();
      return;
    }

    createGameTopic
      .mutateAsync({
        name: topicName,
        body,
        type: "General",
        parent: undefined,
        parentId: undefined,
        communal: false,
        implementation: "Markdown",
      })
      .then(() => {
        enqueueSnackbar(`Topic "${topicName}" created`, { variant: "success" });
        onComplete();
      })
      .catch(() => {
        enqueueSnackbar(`An error occurred importing "${topicName}" topic`, { variant: "error" });
        onError();
      });
  }, [body, createGameTopic, gameId, onComplete, onError, onStart, topicName, topics, enqueueSnackbar]);

  return null;
};

/**
 * @typedef {'overview'|'components'} GameTopicImportKind
 */

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("./parseExport").NestiFyzGame} props.nestifyzGame
 * @param {(kind: GameTopicImportKind) => void} props.onItemStart
 * @param {(kind: GameTopicImportKind) => void} props.onItemComplete
 * @param {(kind: GameTopicImportKind) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportGameTopicsUpload = ({
  gameId,
  nestifyzGame,
  onItemStart,
  onItemComplete,
  onItemError,
  onComplete,
  onError,
}) => {
  const { isLoading } = useGameTopics(gameId);
  const [topicIndex, setTopicIndex] = useState(0);

  const topicsToImport = [
    hasImportableOverviewTopic(nestifyzGame)
      ? {
          kind: /** @type {GameTopicImportKind} */ ("overview"),
          name: NESTIFYZ_OVERVIEW_TOPIC_NAME,
          body: buildNestiFyzOverviewTopicBody(nestifyzGame),
        }
      : null,
    hasImportableComponentsTopic(nestifyzGame)
      ? {
          kind: /** @type {GameTopicImportKind} */ ("components"),
          name: NESTIFYZ_COMPONENTS_TOPIC_NAME,
          body: buildNestiFyzComponentsTopicBody(nestifyzGame),
        }
      : null,
  ].filter((topic) => topic !== null);

  if (isLoading) {
    return null;
  }

  if (topicsToImport.length === 0) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  const currentTopic = topicsToImport[topicIndex];

  if (!currentTopic) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  const advance = () => {
    if (topicIndex + 1 >= topicsToImport.length) {
      onComplete();
      return;
    }

    setTopicIndex((index) => index + 1);
  };

  return (
    <CreateImportedGameTopic
      key={currentTopic.kind}
      gameId={gameId}
      topicName={currentTopic.name}
      body={currentTopic.body}
      onStart={() => {
        onItemStart(currentTopic.kind);
      }}
      onComplete={() => {
        onItemComplete(currentTopic.kind);
        advance();
      }}
      onError={() => {
        onItemError(currentTopic.kind);
        onError();
      }}
    />
  );
};
